/*
 * Created by Ali Simsek on 5/20/20, 11:50 AM
 * Copyright (c) 2020 Carla Car Rental, Inc. All rights reserved.
 * Last modified 5/20/20, 11:42 AM
 */

import SessionManager from './SessionManager'
import util from '../util'
import debug from 'debug'

const log = debug('carlin:api')

/**
 * wait for SessionManager to fetch device id from backend or requests will fail
 * @returns {Promise<boolean>}
 */
const waitAuthKey = async () => {
  while (!SessionManager.getAuthKey()) {
    log('waiting for device id')
    await util.sleep(250)
  }
  return true
}

const getOrigin = () => {
  if (util.isLocal() && util.isBrowser()) {
    return window.location.origin
  }
  if (util.isProduction()) return 'https://carlin.rentcarla.com'
  else if (util.isDevelopment()) return 'https://carlin-dev.rentcarla.com'
  throw new APIError('API origin not found')
}

const getEventsOrigin = () => {
  if (util.isProduction()) return 'https://akzhdplyec.execute-api.eu-west-1.amazonaws.com/default/event-producer-prod'
  else if (util.isDevelopment())
    return 'https://yeoium3i79.execute-api.eu-west-1.amazonaws.com/default/event-producer-http'
  throw new APIError('API origin not found')
}

const send = async (url, options = {}) => {
  const data = options.data || null
  const method = options.method || 'POST'
  const json = options.json !== false
  const headers = options.headers || null
  const isSecure = options.isSecure || true
  const mode = options.mode || 'cors'

  log(`sending request to "${url}", method: "${method}", data:`, data)

  const req = {
    headers: {},
    method: method,
    mode: mode
  }
  if (headers) req.headers = headers
  if (json) req.headers['content-type'] = 'application/json'
  if (data) req.body = JSON.stringify(data)

  req.headers['Access-Control-Allow-Origin'] = '*'
  req.headers['Access-Control-Allow-Methods'] = 'GET,POST,PUT,PATCH'
  req.headers['Access-Control-Allow-Credentials'] = true
  req.headers['Access-Control-Allow-Headers'] = 'Authorization,deviceId,language,userCountry'
  log(`isSecure ${isSecure}`)
  if (isSecure) {
    req.headers['Authorization'] = SessionManager.getAuthKey()
    req.headers['deviceId'] = SessionManager.getCarlinDeviceId()
    req.headers['language'] = 'en'
    req.headers['userCountry'] = SessionManager.getUserCountry()
  }

  const res = await fetch(url, req)

  if (res.status >= 400) {
    throw new APIError(`${res.status}: ${res.statusText}`, res.status)
  }
  // no content, example: "no cars"
  if (res.status === 204) {
    return null
  }

  const contentType = res.headers.get('content-type')
  if (contentType && contentType.indexOf('application/json') !== -1) {
    return await res.json()
  } else {
    return await res.text()
  }
}

/**
 * extension of Error class with status and message received from API
 */
export class APIError extends Error {
  constructor(message, status) {
    super()
    this.message = message
    this.status = status
  }
}

const API = {
  async searchNearbyCars(pickupDateTime, dropOffDateTime, age, nearbySearchId, pickupBrokerLocations) {
    return await send(`${getOrigin()}/s/searchNearbyCars/`, {
      data: {
        pickupDateTime,
        dropOffDateTime,
        age,
        nearbySearchId,
        pickupBrokerLocations,
        sameDropOff: 'true'
      }
    })
  },

  async searchNearbyBrokerLocations(lat, lon, radius) {
    return await send(`${getOrigin()}/s/searchNearbyBrokerLocations/`, {
      data: {
        latitude: lat,
        longitude: lon,
        radius: radius
      }
    })
  },

  async registerToBackend() {
    return await send(`${getOrigin()}/registerDevice`, {
      data: {
        country: SessionManager.getUserCountry(),
        language: 'en',
        oldDeviceId: SessionManager.getDeviceId(),
        os: 'web'
      },
      isSecure: false
    })
  },

  async updateDevice(deviceToken) {
    await waitAuthKey()
    return await send(`${getOrigin}/s/updateDevice`, {
      method: 'PATCH',
      data: {
        deviceToken: deviceToken
      }
    })
  },

  async searchPlaces(queryString) {
    if (!queryString) return []
    await waitAuthKey()
    return await send(`${getOrigin()}/s/searchPlaces/${queryString}`, {
      method: 'GET'
    })
  },

  async getPromoCode(queryString) {
    await waitAuthKey()
    const authKey = SessionManager.getAuthKey()
    const deviceKey = SessionManager.getCarlinDeviceId()

    return await send(`${getOrigin()}/s/getPromoDetails/?promoCode=${queryString}`, {
      method: 'GET',
      headers: {
        deviceId: deviceKey,
        Authorization: authKey
      }
    })
  },

  async searchCars(csArgs) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/searchPartnerCars/`, {
      data: csArgs
    })
  },

  async searchAds(csArgs) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/searchAds/`, {
      data: csArgs
    })
  },

  async getNoCarsOptions(csArgs) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/getNoCarsRecommendation/`, {
      data: csArgs
    })
  },

  async getAncillaryProducts(carRentalProduct) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/ancillaryProduct/`, {
      data: carRentalProduct
    })
  },

  async makeModifications(carRentalProduct) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/makePrecheckoutModifications/`, {
      data: carRentalProduct
    })
  },

  async completeReservation(xo2Details) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/completeReservation`, {
      data: xo2Details,
      headers: {
        channel: "PARTNER"
      }
    })
  },

  async getClosestLocation() {
    return await send(`${getOrigin()}/getClosestLocation`, {
      method: 'GET',
      isSecure: false
    })
  },

  async searchWithId(searchId) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/searchWithId/?searchId=${searchId}`, {
      method: 'GET'
    })
  },

  async getDeeplinkDetails(productId) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/getDeeplinkDetails/${productId}`, {
      method: 'GET'
    })
  },

  async addDetails(xo1Details) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/providedDetails/`, {
      data: xo1Details
    })
  },

  async sendFeedbackForReservation(id, reservationId, score) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/feedback/${id}/${reservationId}?score=${score}`, {
      method: 'GET',
      json: false
    })
  },

  async sendEvent(eventParams) {
    await waitAuthKey()
    return await send(getEventsOrigin(), {
      data: eventParams,
      mode: 'no-cors'
    })
  },

  async getPlace(areaId) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/getPlace/${areaId}`, {
      method: 'GET'
    })
  },

  async searchAvailableAreas(pickupDateTime, dropOffDateTime, pickupLocation, dropOffLocation, age) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/searchAvailableAreas/`, {
      data: { pickupDateTime, dropOffDateTime, pickupLocation, dropOffLocation, age }
    })
  },

  async requestCouponCode(body) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/grantCouponCode`, {
      method: 'POST',
      data: body
    })
  },

  async retrieveReservation(args) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/retrieveSingleReservation`, {
      method: 'POST',
      data: args
    })
  },

  async askForSupport(args) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/support`, {
      method: 'POST',
      data: args
    })
  },

  async getFAQs(key) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/selfHelpChildItems/${key}`, {
      method: 'GET'
    })
  },

  async getPopularLocations() {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/getRecommendedDestinations`, {
      method: 'GET'
    })
  },

  async getLandingInfo(locationID) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/getLocalizedLandingInfo/${locationID}`, {
      method: 'GET'
    })
  },

  async getDynamicPopularLocations(id) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/getRecommendedLocationsForArea/${id}`, {
      method: 'GET'
    })
  },

  async getThreeDSInfo(ThreeDSWebRequest) {
    await waitAuthKey()
    return await send(`${getOrigin()}/t/threeDSWeb/`, {
      method: 'POST',
      data: ThreeDSWebRequest
    })
  },

  async check3DsStatus(token, productID) {
    await waitAuthKey()
    return await send(`${getOrigin()}/t/process3Ds/${token}&${productID}`, {
      method: 'GET'
    })
  },

  // returns productID of a spesific car -> for 3Ds
  async getSelectedCar(carData) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/selectedCar/`, {
      method: 'POST',
      data: carData
    })
  },

  async saveUserCardInfo(WebUserCardInfo) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/saveUserCardInfo/`, {
      method: 'POST',
      data: WebUserCardInfo
    })
  },

  async getCarlaPlusDetailInfo(email) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/getCarlaPlusDetails/?email=${email}`, {
      method: 'GET'
    })
  },

  async searchHotel(params) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/searchHotelsV2`, {
      method: 'POST',
      data: params
    })
  },

  async getHotelFilters() {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/hotelFilters`, {
      method: 'GET'
    })
  },

  async getCarFilters(bundleId) {
    if (!bundleId) return []
    await waitAuthKey()
    return await send(`${getOrigin()}/s/carFilters/?bundleId=${bundleId}`, {
      method: 'GET'
    })
  },

  async getHotelLocations(queryString) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/searchHotelPlaces/?searchString=${queryString}`, {
      method: 'GET'
    })
  },

  async getHotelDetail(params) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/getDetailsForHotelV2`, {
      method: 'POST',
      data: params
    })
  },

  async providedHotelDetails(params) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/providedHotelDetails`, {
      method: 'POST',
      data: params
    })
  },

  async completeHotelBooking(xo2Details) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/completeHotelBooking`, {
      data: xo2Details
    })
  },

  async searchBundle(params) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/searchBundle`, {
      method: 'POST',
      data: params
    })
  },

  async getBundleFilters() {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/filterProducts`, {
      method: 'GET'
    })
  },

  async providedBundleDetails(params) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/providedBundleDetails`, {
      method: 'POST',
      data: params
    })
  },

  async completeHotelBookingV2(xo2Details) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/completeHotelBookingV2`, {
      data: xo2Details
    })
  },

  async getTermsAndConditions() {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/termsAndConditions`, {
      method: 'GET'
    })
  },

  async getPrizeByEmail(email) {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/applyPrize?email=${email}`, {
      method: 'GET'
    })
  },

  async getPrizeDetails() {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/prizeDetails/`, {
      method: 'GET'
    })
  },

  async getReservation() {
    await waitAuthKey()
    return await send(`${getOrigin()}/s/termsAndConditions/`, {
      method: 'GET'
    })
  }
}

export default API
